import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

interface RegenerateSubscriptionLinkRequest {
  tenantId: number;
  noFeePayment?: boolean;
}

interface RegenerateSubscriptionLinkResponse {
  success: boolean;
  message: string;
  achUrl: string;
  cardUrl: string;
  baseAmount?: number;
  checkoutUrl?: string; // Keep for backward compatibility
}

const METHOD = 'POST';
const ENDPOINT = 'unit/:id/regenerate-subscription-link';
export const MUTATION_KEY = (unitId: number) => getQueryKey(METHOD, ENDPOINT, { id: unitId });

export const useRegenerateSubscriptionLink = (
  unitId: number,
  options?: UseMutationOptions<
    RegenerateSubscriptionLinkResponse,
    unknown,
    RegenerateSubscriptionLinkRequest
  >
) => {
  const { session } = useAuth();

  return useMutation<
    RegenerateSubscriptionLinkResponse,
    unknown,
    RegenerateSubscriptionLinkRequest
  >({
    mutationKey: MUTATION_KEY(unitId),
    mutationFn: async (data: RegenerateSubscriptionLinkRequest) => {
      try {
        const response = await handleApiCall<
          RegenerateSubscriptionLinkResponse,
          RegenerateSubscriptionLinkRequest
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            id: unitId.toString(),
          }),
          data
        );

        return response;
      } catch (err) {
        enqueueSnackbar('Failed to regenerate subscription links.', { variant: 'error' });
        throw err;
      }
    },
    ...options,
  });
};
